import { CustomerGroupResponse } from "@/models/response/customerGroup";
import type { SmartAudienceChoice } from "@/store/message";
import { Customer } from "@/models/response/customer";
import { discountGroup, SmsTemplate } from "@/services/discount/type";
import { DateObject } from "react-multi-date-picker";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

export enum DiscountGroupType {
  customerGroup,
  discountGroup,
  smartAudience,
  // Chosen customers, or every customer of the salon.
  customers,
  all,
}

type AddDiscountGroupPayload = {
  id: string;
  count?: number;
  discountGroup: discountGroup;
};

type AddCustomerGroupPayload = {
  id: string;
  count?: number;
  customerGroup: CustomerGroupResponse;
};

type SetSmartAudiencePayload = {
  id: string;
  smartAudience: SmartAudienceChoice;
};

type SetSmsTemplatePayload = {
  smsTemplate: SmsTemplate;
};

type SetDiscountTypePayload = {
  type: string;
};

type SetAmountPayload = {
  amount: string;
  minimumAmount: string;
};
type SetPercentagePayload = {
  percentage: string;
  minimumAmount: string;
  maximumAmount: string;
};

type SetDateTimePayload = {
  date?: string;
  customDate?: DateObject;
  time?: string;
};

export type Discount = {
  id?: string;
  count?: number;
  groupType?: DiscountGroupType;
  discountGroup?: discountGroup;
  customerGroup?: CustomerGroupResponse;
  smartAudience?: SmartAudienceChoice;
  customers?: Customer[];
  smsTemplate?: SmsTemplate;
  discountType?: string;
  amount?: string;
  percentage?: string;
  minimumAmount?: string;
  maximumAmount?: string;
  date?: string;
  customDate?: DateObject;
  time?: string;
  addDiscountGroup: (command: AddDiscountGroupPayload) => void;
  addCustomerGroup: (customerGroup: AddCustomerGroupPayload) => void;
  setSmartAudience: (command: SetSmartAudiencePayload) => void;
  setCustomers: (command: { id: string; all: boolean; customers: Customer[]; count: number }) => void;
  setSmsTemplate: (command: SetSmsTemplatePayload) => void;
  setDiscountType: (command: SetDiscountTypePayload) => void;
  setAmount: (command: SetAmountPayload) => void;
  setPercentage: (command: SetPercentagePayload) => void;
  setDateTime: (command: SetDateTimePayload) => void;
  clearDiscount: () => void;
};

const useDiscountStore = create<Discount>()(
  devtools(
    persist(
      (set) => ({
        type: undefined,
        customerGroup: undefined,
        group: undefined,
        addDiscountGroup: (command) =>
          set((state) => ({
            ...state,
            id: command?.id,
            count: command.count,
            discountGroup: command.discountGroup,
            groupType: DiscountGroupType.discountGroup,
            customerGroup: undefined,
            smartAudience: undefined,
          })),
        addCustomerGroup: (command) =>
          set((state) => ({
            ...state,
            id: command?.id,
            count: command.count,
            customerGroup: command.customerGroup,
            groupType: DiscountGroupType.customerGroup,
            discountGroup: undefined,
            smartAudience: undefined,
          })),
        setCustomers: (command) =>
          set((state) => ({
            ...state,
            id: command.id,
            count: command.count,
            customers: command.all ? [] : command.customers,
            groupType: command.all ? DiscountGroupType.all : DiscountGroupType.customers,
            customerGroup: undefined,
            discountGroup: undefined,
            smartAudience: undefined,
          })),
        setSmartAudience: (command) =>
          set((state) => ({
            ...state,
            id: command.id,
            count: command.smartAudience.customerCount ?? 0,
            smartAudience: command.smartAudience,
            groupType: DiscountGroupType.smartAudience,
            customerGroup: undefined,
            discountGroup: undefined,
            customers: undefined,
          })),
        setSmsTemplate: (command) =>
          set((state) => ({
            ...state,
            smsTemplate: command.smsTemplate,
          })),
        setDiscountType: (command) =>
          set((state) => ({
            ...state,
            discountType: command.type,
          })),
        setAmount: (command) =>
          set((state) => ({
            ...state,
            amount: command.amount,
            minimumAmount: command?.minimumAmount,
          })),
        setPercentage: (command) =>
          set((state) => ({
            ...state,
            percentage: command.percentage,
            minimumAmount: command?.minimumAmount,
            maximumAmount: command?.maximumAmount,
          })),
        setDateTime: (command) =>
          set((state) => ({
            ...state,
            date: command?.date,
            customDate: command?.customDate,
            time: command?.time,
          })),
        clearDiscount: () =>
          set(() => ({
            id: undefined,
            count: undefined,
            groupType: undefined,
            discountGroup: undefined,
            customerGroup: undefined,
            smartAudience: undefined,
            customers: undefined,
            smsTemplate: undefined,
            discountType: undefined,
            amount: undefined,
            percentage: undefined,
            minimumAmount: undefined,
            maximumAmount: undefined,
            date: undefined,
            customDate: undefined,
            time: undefined,
          })),
      }),
      { name: "createDiscountState" }
    )
  )
);

export default useDiscountStore;
